import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDirection } from '../../../contexts/DirectionContext';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { BaseMediaThumbnailProps } from '../MediaThumbnail';
import { GRID_CONFIG } from '../../../constants/grid';

interface MediaSliderProps<T extends BaseMediaThumbnailProps> {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  title?: string;
  onSeeAllClick?: () => void;
  type?: 'video' | 'shorts';
  className?: string;
  titleClassName?: string;
  contentClassName?: string;
}

export const MediaSlider = <T extends BaseMediaThumbnailProps>({
  items,
  renderItem,
  title,
  onSeeAllClick,
  type = 'video',
  className = '',
  titleClassName = '',
  contentClassName = '',
}: MediaSliderProps<T>) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [itemWidth, setItemWidth] = useState(0);
  const { isDark } = useTheme();
  const { isRTL } = useDirection();
  const { t } = useTranslation();

  const config = type === 'shorts' ? GRID_CONFIG.content.shorts : GRID_CONFIG.content.video;

  const updateScrollButtons = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      if (!scrollContainerRef.current) return;

      const { scrollLeft, scrollWidth, clientWidth } = container;
      const maxScroll = scrollWidth - clientWidth;

      if (isRTL) {
        setIsScrolled(scrollLeft < -1);
        setCanScrollBack(scrollLeft < -1);
        setCanScrollForward(Math.abs(scrollLeft) < maxScroll - 1);
      } else {
        setIsScrolled(scrollLeft > 1);
        setCanScrollBack(scrollLeft > 1);
        setCanScrollForward(scrollLeft < maxScroll - 1);
      }
    });
  }, [isRTL]);

  const calculateItemWidth = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const availableWidth = container.clientWidth;
    const isMobile = availableWidth < GRID_CONFIG.breakpoints.sm;
    const spacing = isMobile ? GRID_CONFIG.mobileSpacing : GRID_CONFIG.spacing;
    
    // Calculate how many items can fit while respecting min/max width constraints
    const maxItemsWithMinWidth = Math.floor(availableWidth / config.minWidth);
    const maxItemsWithMaxWidth = Math.ceil(availableWidth / config.maxWidth);
    
    // Choose number of items that will fit best
    const itemsToFit = Math.max(
      maxItemsWithMaxWidth,
      Math.min(maxItemsWithMinWidth, type === 'shorts' ? 6 : 4)
    );

    // Calculate exact width to fit items perfectly
    const gapSpace = spacing * (itemsToFit - 1);
    const width = Math.floor((availableWidth - gapSpace) / itemsToFit);

    setItemWidth(width);
  }, [config.minWidth, config.maxWidth, type]);

  useEffect(() => {
    calculateItemWidth();
    const handleResize = () => {
      calculateItemWidth();
      updateScrollButtons();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateItemWidth, updateScrollButtons]);

  const scroll = useCallback((direction: 'prev' | 'next') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { clientWidth } = container;
    const isMobile = clientWidth < GRID_CONFIG.breakpoints.sm;
    const spacing = isMobile ? GRID_CONFIG.mobileSpacing : GRID_CONFIG.spacing;
    
    const itemsPerPage = Math.floor((clientWidth + spacing) / (itemWidth + spacing));
    const scrollAmount = (itemWidth + spacing) * Math.max(1, Math.floor(itemsPerPage * 0.8));
    
    const isNext = direction === 'next';
    const multiplier = isRTL ? -1 : 1;
    const targetScroll = container.scrollLeft + (scrollAmount * (isNext ? 1 : -1) * multiplier);

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }, [isRTL, itemWidth]);

  const containerClasses = `
    relative w-full
    ${className}
    ${isDark ? 'dark' : ''}
    ${isRTL ? 'rtl' : 'ltr'}
  `.trim();

  const buttonClasses = `
    absolute top-1/2 -translate-y-1/2
    w-10 h-10 sm:w-12 sm:h-12
    hidden sm:flex
    items-center justify-center
    rounded-full
    z-50
    transition-all duration-300
    before:absolute before:inset-0 before:rounded-full before:-z-10
    before:bg-background-primary before:opacity-70 hover:before:opacity-100
    before:transition-opacity before:duration-300
    shadow-[0_2px_6px_rgba(0,0,0,0.1)]
    hover:shadow-[0_4px_12px_rgba(0,0,0,0.15)]
  `.trim();

  return (
    <div className={containerClasses}>
      {title && (
        <div className={`flex items-center justify-between mb-1 sm:mb-4 px-0 sm:px-4 ${titleClassName}`}>
          <h2 className="text-lg sm:text-xl font-semibold text-text-primary">
            {title}
          </h2>
          {onSeeAllClick && (
            <button 
              onClick={onSeeAllClick}
              className="text-xs sm:text-sm text-text-secondary hover:text-text-secondary transition-colors duration-200"
            >
              {t('common.seeAll')}
            </button>
          )}
        </div>
      )}

      <div className="relative">
        <button
          onClick={() => scroll('prev')}
          className={`
            ${buttonClasses}
            ${isRTL ? 'right-0' : 'left-0'}
            ${canScrollBack ? 'opacity-100' : 'opacity-0 pointer-events-none'}
          `}
          aria-label={t('common.previous')}
        >
          <Icon
            icon={isRTL ? 'ph:caret-right' : 'ph:caret-left'}
            className="w-5 h-5 sm:w-6 sm:h-6 text-text-primary"
          />
        </button>

        <div className="sm:mx-6">
          <div
            ref={scrollContainerRef}
            className={`
              overflow-x-auto
              scrollbar-none
              [scrollbar-width:none]
              [-ms-overflow-style:none]
              [&::-webkit-scrollbar]:hidden
              scroll-smooth
              snap-x snap-mandatory
              flex gap-2 sm:gap-4
              py-1 sm:py-2
              px-2 sm:px-0
              ${contentClassName}
            `}
            onScroll={updateScrollButtons}
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            {items.map((item, index) => (
              <div 
                key={item.id || index} 
                style={{ width: itemWidth ? `${itemWidth}px` : 'auto' }}
                className="snap-start flex-shrink-0"
              >
                {renderItem(item)}
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => scroll('next')}
          className={`
            ${buttonClasses}
            ${isRTL ? 'left-0' : 'right-0'}
            ${canScrollForward ? 'opacity-100' : 'opacity-0 pointer-events-none'}
          `}
          aria-label={t('common.next')}
        >
          <Icon
            icon={isRTL ? 'ph:caret-left' : 'ph:caret-right'}
            className="w-5 h-5 sm:w-6 sm:h-6 text-text-primary"
          />
        </button>
      </div>
    </div>
  );
};

export default MediaSlider;